/* eslint-disable react/no-danger */
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Collapse, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import sanitizeHtml from 'sanitize-html';

import ProductDescriptionSkeleton from './productDescriptionSkeleton';

import ReadMoreButton from '@components/atoms/readMore';
import ResultPanelEmptyData from '@components/store/data-display/resultPanel/resultPanelEmptyData';

const DESCRIPTION_MAX_HEIGHT = 200;

type ProductDescriptionProps = {
  description: string;
  seoDescription?: string;
  commercialMessage?: string;
  isDetailLoading?: boolean;
};

const ProductDescription: FC<ProductDescriptionProps> = ({
  description,
  seoDescription,
  commercialMessage,
  isDetailLoading,
}) => {
  const { t } = useTranslation('common');

  const collapseRef = useRef<HTMLDivElement>();

  const [expandDescription, setExpandDescription] = useState<boolean>(false);
  const [descriptionHeight, setDescriptionHeight] = useState<number>(0);

  const commercialMessageSection = useMemo(() => {
    if (!commercialMessage) {
      return null;
    }

    const sanitizedHtmlCommercialMessage = sanitizeHtml(commercialMessage);

    return (
      <div
        style={{ fontSize: 22, fontFamily: 'Lanna' }}
        dangerouslySetInnerHTML={{ __html: sanitizedHtmlCommercialMessage }}
      />
    );
  }, [commercialMessage]);

  const descriptionSection = useMemo(() => {
    if (!description) {
      return null;
    }

    const sanitizedHtmlDescription = sanitizeHtml(description);

    return (
      <div
        style={{ fontSize: 22, fontFamily: 'Lanna', overflowWrap: 'anywhere' }}
        dangerouslySetInnerHTML={{ __html: sanitizedHtmlDescription }}
      />
    );
  }, [description]);

  useEffect(() => {
    if (collapseRef.current) {
      setDescriptionHeight(collapseRef.current.offsetHeight);
    }
  }, [collapseRef.current, commercialMessageSection, descriptionSection]);

  const content = useMemo(() => {
    return (
      <Box ref={collapseRef}>
        {commercialMessageSection}
        <Typography component="p" variant="body1lanna" fontFamily="Lanna">
          {seoDescription}
        </Typography>
        {descriptionSection}
      </Box>
    );
  }, [commercialMessageSection, descriptionSection, seoDescription]);

  if (!seoDescription && isDetailLoading) {
    return <ProductDescriptionSkeleton />;
  }

  if (!commercialMessageSection && !seoDescription && !descriptionSection) {
    return <ResultPanelEmptyData sx={{ height: 264 }} />;
  }

  return (
    <Box>
      {descriptionHeight > DESCRIPTION_MAX_HEIGHT ? (
        <>
          <Collapse in={expandDescription} collapsedSize={DESCRIPTION_MAX_HEIGHT} sx={{ position: 'relative' }}>
            {content}
            {!expandDescription && (
              <Box
                sx={{
                  width: '100%',
                  height: 80,
                  position: 'absolute',
                  bottom: 0,
                  background: 'linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%)',
                }}
              />
            )}
          </Collapse>
          <Box display="flex" justifyContent="center">
            <ReadMoreButton
              title_expand={t('expand')}
              title_unexpand={t('unexpand')}
              expand={expandDescription}
              onClick={() => setExpandDescription(!expandDescription)}
              icon={false}
            />
          </Box>
        </>
      ) : (
        content
      )}
    </Box>
  );
};

export default ProductDescription;
